import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Transition,
  TransitionGroup,
} from 'react-transition-group';
import { ModalContainer } from './Modal';
import anime from 'animejs';
import createLogger from '../../utils/createLogger';

const DEBUG = false;
const debug = createLogger(DEBUG, `Layouts.js`);


/** -------- App root layout -------- */
class AppLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  render() {
    const { children } = this.props;

    debug.enter(`render`);

    const classes = classnames([
      `layout`,
      `app`,
    ]);

    return (
      <div className={classes}>
        <ModalContainer/>
        { children }
      </div>
    );
  }
}

/** -------- Login page layout -------- */
class LoginLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  render() {
    const { children } = this.props;

    debug.enter(`render`);

    const classes = classnames([
      `layout`,
      `auth`,
    ]);

    return (
      <div className={classes}>
        <main>{ children }</main>
      </div>
    );
  }
}

/** -------- Titles page layout -------- */
class TitlesLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  render() {
    const {children} = this.props;

    debug.enter(`render`);

    const classes = classnames([
      `layout`,
    ]);

    return (
        <div className={classes}>
          <main>{ children }</main>
        </div>
    )
  }
}

/** -------- Agencies page layout -------- */
class AgenciesLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  render() {
    const {children} = this.props;

    debug.enter(`render`);

    const classes = classnames([
      `layout`,
    ]);

    return (
        <div className={classes}>
          <main>{ children }</main>
        </div>
    )
  }
}

/** ------ Indeed History page layout ------ */
class OpenWebLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  render () {
    const {children} = this.props;

    const classes = classnames([
      `layout`
    ]);

    return (
      <div className={classes}>
        <main> {children} </main>
      </div>
    )
  }
}


const animeProps = {
  duration: 500,
  easing: `easeInOutQuad`,
  delay: 100,
};

const createEnterTransition = (flex, dir) => (node) => {
  const { basis, grow, shrink } = flex;
  let config = {
    ...animeProps,
    targets: node,
    opacity: 1,
  };
  if (dir)    config[dir] = [`-100vw`, 0];
  if (basis)  config[`flex-basis`] = [0, basis];
  if (grow)   config[`flex-grow`] = [0.01, grow];
  if (shrink) config[`flex-shrink`] = [0.01, shrink];

  return anime(config);
};

const createExitTransition = (flex, dir) => (node) => {
  const [grow, shrink, basis] = flex;
  let config = {
    ...animeProps,
    targets: node,
    opacity: 0,
  };
  if (dir)    config[dir] = [0, `-100vw`];
  if (basis)  config[`flex-basis`] = [basis, 0];
  if (grow)   config[`flex-grow`] = [grow, 0.01];
  if (shrink) config[`flex-shrink`] = [shrink, 0.01];

  return anime(config);
};

const createTransitionConfig = (component) => ({
  key: component.key,
  timeout: animeProps.duration,
  onEnter: createEnterTransition(component.flex, component.dir),
  onExit: createExitTransition(component.flex, component.dir),
  appear: false,
});

const mapChildrenToTemplate = (template) => (children) => {
  let map = {};
  React.Children.forEach(children, (child) => {
    if (!child)
      return;

    template.forEach((role) => {
      if (child.type.role === role) {
        map[role] = child
      }
    });
  });
  return map;
};


/** -------- Job listing page layout -------- */
class JobListingLayout extends React.Component {

  static propTypes = {
    children: PropTypes.node,
  };

  static mapChildren = mapChildrenToTemplate([
    `modal`,
    `header`,
    `filters`,
    `list`,
    `detail`,
    `search`,
  ]);

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.comp !== undefined){
      this.comp.scrollTop = 0;
    }
  }


  render() {
    const { location } = this.props;

    debug.enter(`render`);
    debug.log(`  location:`, location);

    const children = JobListingLayout.mapChildren(this.props.children);

    const classes = classnames([
      `layout`,
      `job-listing`,
    ]);

    debug.log(`  children:`, children);

    const bodyArray = [];
    bodyArray.push(!!children.filters ? {
        node: children.filters,
        key: `filters`,
        dir: `left`,
        flex: [0, 1, `15rem`],
    } : null);
    bodyArray.push({
      node: children.list,
      key: `list`,
      dir: `left`,
      flex: [1, 1, `20%`]
    });
    bodyArray.push(!!children.detail ? {
      node: children.detail,
      key: `detail`,
      dir: `right`,
      flex: [1, 1, `40%`],
    } : null);
    bodyArray.push(!!children.search ? {
      node: children.search,
      key: `search`,
      dir: `right`,
      flex: [1, 1, `40%`],
    } : null);


    return (
      <div className={classes}>
        { children.modal } { /* modal portals up to ModalContainer in AppLayout */ }

        { children.header } { /* TODO: Move up to JobsLayout? */ }

        <TransitionGroup component="main">
          {
            bodyArray.map((child) => (
              !!child ? (
                <Transition {...createTransitionConfig(child)}>
                  <div className={`${child.key}-container`}
                       ref={(c) => this.comp = c}
                       id={`${child.key}-container`}>
                    { child.node }
                  </div>
                </Transition>
              ) : null
            ))
          }
        </TransitionGroup>
      </div>
    );
  }
}

/*{
<div className="body">
          { !children.filters ? null : (
            <div className="aside-container">
              { children.filters }</div>
          )}

          <div className="list-container">
            { children.list }</div>

          { !children.detail ? null : (
            <div className="detail-container">
              { children.detail }</div>
          )}

          { !children.search ? null : (
            <div className="search-container">
              { children.search }</div>
          )}
        </div>

    const arr = [
      children.filters ? { key: `aside`, width: `15rem`, Child: children.filters } : null,
      children.list ? { key: `list`, width: `20%`, Child: children.list } : null,
      children.detail ? { key: `detail`, width: `40%`, Child: children.detail } : null,
      children.search ? { key: `search`, width: `40%`, Child: children.search } : null,
    ];

    <TransitionGroup>
          {
            arr.map(({key, width, Child}) => (
              !!Child ? (
                <Transition {...config(key, width)}>
                  <div className={`${key}-container`}>
                    <Child/>
                  </div>
                </Transition>
              ) : null
            ))
          }
        </TransitionGroup>

!children.filters ? null : (
            <Transition {...config(`aside`, `15rem`)}>
              <div className="aside-container">
                { children.filters }
              </div>
            </Transition>
          )}

          { !children.list ? null : (
            <Transition {...config(`list`, `20%`)}>
              <div className="list-container">
                { children.list }
              </div>
            </Transition>
          )}

          { !children.detail ? null : (
            <Transition {...config(`detail`, `40%`)}>
              <div className="detail-container">
                { children.detail }
              </div>
            </Transition>
          )}

          { !children.search ? null : (
            <Transition {...config(`search`, `40%`)}>
              <div className="search-container">
                { children.search }
              </div>
            </Transition>
          )}*/

export {
  AppLayout,
  LoginLayout,
  JobListingLayout,
  TitlesLayout,
  AgenciesLayout,
  OpenWebLayout,
};



// WEBPACK FOOTER //
// ./src/components/static/Layouts.js